import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FeatherAlertCircle } from "@subframe/core";
import { Button } from "../../../../ui/components/Button";
import { useHomeSummary } from "../hooks/useHomeSummary";
import { resolveTaskTarget } from "../navigation/taskTargets";
import { buildTaskGroups, TaskRowView } from "../viewmodels/buildTaskGroups";
import { FilingsCalendarCard } from "../components/FilingsCalendarCard";
import { HomeHeader } from "../components/HomeHeader";
import { TaskGroup } from "../components/TaskGroup";

/** Where the filings rail sends the user. */
const COMPLIANCE_PATH = "/dashboard/compliance";

const HomePageSkeleton: React.FC = () => (
  <div className="flex w-full grow shrink-0 basis-0 items-center justify-center px-8 py-8">
    <div className="flex flex-col items-center gap-4">
      <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-brand-primary" />
      <span className="text-body font-body text-subtext-color">
        Loading your dashboard...
      </span>
    </div>
  </div>
);

const HomePageError: React.FC<{ message: string; onRetry: () => void }> = ({
  message,
  onRetry,
}) => (
  <div className="flex w-full grow shrink-0 basis-0 items-center justify-center px-8 py-8">
    <div className="flex max-w-md flex-col items-center gap-4 text-center">
      <FeatherAlertCircle className="text-heading-1 font-heading-1 text-error-600" />
      <span className="text-heading-3 font-heading-3 text-default-font">
        We couldn't load your dashboard
      </span>
      <span className="text-body font-body text-subtext-color">{message}</span>
      <Button onClick={onRetry}>Try again</Button>
    </div>
  </div>
);

/**
 * HomePage — the v1 accountant work queue at `/v1`.
 *
 * Renders whatever needs a human today, grouped by the area it lives in, plus
 * the filings rail. Everything comes from one call to
 * `GET /api/v1/home/summary`.
 *
 * Runs alongside the existing `/dashboard/reco` Reconciliation Dashboard rather
 * than replacing it (issue #636).
 */
export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { summary, loading, error, refresh } = useHomeSummary();

  const groups = useMemo(
    () => (summary ? buildTaskGroups(summary) : []),
    [summary]
  );

  if (loading && !summary) {
    return <HomePageSkeleton />;
  }

  if (error && !summary) {
    return <HomePageError message={error} onRetry={refresh} />;
  }

  if (!summary) {
    return null;
  }

  const handleSelectRow = (row: TaskRowView): void => {
    navigate(resolveTaskTarget(row.target));
  };

  return (
    <div className="flex w-full flex-col items-start gap-6 px-8 py-8">
      {/*
        A refresh failure with data already on screen is shown inline; the stale
        counts stay visible because they are more useful than an empty page.
      */}
      {error && (
        <div className="flex w-full items-center justify-between gap-4 rounded-md border border-solid border-warning-200 bg-warning-50 px-4 py-3">
          <span className="text-body font-body text-warning-800">
            Couldn't refresh: {error}
          </span>
          <Button size="small" variant="neutral-tertiary" onClick={refresh}>
            Retry
          </Button>
        </div>
      )}

      <div className="flex w-full flex-wrap items-start gap-8">
        <div className="flex min-w-[320px] grow shrink basis-[560px] flex-col items-start gap-8">
          <HomeHeader
            userName={summary.user.name}
            companyName={summary.company.name}
            totalOpenItems={summary.totalOpenItems}
          />

          {groups.map((group) => (
            <TaskGroup key={group.id} group={group} onSelectRow={handleSelectRow} />
          ))}
        </div>

        <div className="flex w-full min-w-[280px] shrink-0 basis-[340px] flex-col items-start md:w-auto">
          <FilingsCalendarCard
            filings={summary.filings}
            onSelectFiling={() => navigate(COMPLIANCE_PATH)}
          />
        </div>
      </div>
    </div>
  );
};
